import React, { useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  FileSearch,
  Upload,
  FileText,
  Loader2,
  Check,
  AlertCircle,
  Eye,
  Download,
  Copy,
  RefreshCw,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_TYPES = [
  { value: 'management_accounts', label: 'Management Accounts', description: 'Monthly/quarterly management reports' },
  { value: 'income_statement', label: 'Income Statement', description: 'Profit & Loss statements' },
  { value: 'balance_sheet', label: 'Balance Sheet', description: 'Financial position statements' },
  { value: 'bank_statement', label: 'Bank Statement', description: 'Bank account statements' },
  { value: 'invoice', label: 'Invoice', description: 'Invoices and bills' },
  { value: 'general', label: 'General Document', description: 'Any other document type' }
];

export default function AdminOCRPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  // State
  const [activeTab, setActiveTab] = useState('analyze');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [documentType, setDocumentType] = useState('management_accounts');
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF, JPEG, PNG, or WebP file');
      return;
    }

    // Validate file size (20MB for PDFs)
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File too large. Maximum 20MB.');
      return;
    }

    setSelectedFile(file);
    // For PDFs, show a placeholder preview; for images, create object URL
    if (file.type === 'application/pdf') {
      setPreviewUrl(null); // PDFs can't be previewed as images
    } else {
      setPreviewUrl(URL.createObjectURL(file));
    }
    setResult(null);
    setError(null);
  };

  // Analyze document
  const handleAnalyze = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setProcessing(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', documentType);

      const response = await axios.post(`${API_URL}/api/admin/ocr/analyze`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000 // 60 second timeout for OCR
      });

      setResult(response.data);
      toast.success('Document analyzed successfully');
    } catch (err) {
      console.error('OCR error:', err);
      setError(err.response?.data?.detail || err.message || 'Analysis failed');
      toast.error('Analysis failed');
    } finally {
      setProcessing(false);
    }
  };

  // Load history
  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/ocr/results?limit=50`, { headers });
      setHistory(response.data.results || []);
    } catch (err) {
      console.error('Error loading history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, [token]);

  // Copy JSON to clipboard
  const copyToClipboard = (data) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    toast.success('Copied to clipboard');
  };

  // Clear form
  const clearForm = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setResult(null);
    setError(null);
  };

  return (
    <div className="space-y-6" data-testid="admin-ocr-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileSearch className="h-6 w-6" />
            Document OCR & Analysis
          </h1>
          <p className="text-gray-500 mt-1">
            Extract and analyze data from financial documents using AI
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => {
        setActiveTab(v);
        if (v === 'history') loadHistory();
      }}>
        <TabsList>
          <TabsTrigger value="analyze">Analyze Document</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="analyze" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upload Section */}
            <Card>
              <CardHeader>
                <CardTitle>Upload Document</CardTitle>
                <CardDescription>
                  Select an image of a financial document to analyze
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Document Type */}
                <div>
                  <Label>Document Type</Label>
                  <Select value={documentType} onValueChange={setDocumentType}>
                    <SelectTrigger className="mt-1" data-testid="doc-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div>
                            <div className="font-medium">{type.label}</div>
                            <div className="text-xs text-gray-500">{type.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* File Upload */}
                <div>
                  <Label>Document Image</Label>
                  <div className="mt-1 border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                    <Input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png,image/webp"
                      onChange={handleFileSelect}
                      className="max-w-xs mx-auto"
                      data-testid="file-input"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      JPEG, PNG or WebP • Max 10MB
                    </p>
                  </div>
                </div>

                {/* Preview */}
                {selectedFile && (
                  <div>
                    <Label>Selected File</Label>
                    <div className="mt-1 border rounded-lg overflow-hidden bg-gray-50">
                      {previewUrl ? (
                        <img
                          src={previewUrl}
                          alt="Document preview"
                          className="max-h-64 mx-auto object-contain"
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center p-8 text-gray-500">
                          <FileText className="h-12 w-12 mb-2 text-red-500" />
                          <p className="font-medium">{selectedFile.name}</p>
                          <p className="text-xs mt-1">PDF document ready for analysis</p>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedFile?.name} ({(selectedFile?.size / 1024).toFixed(1)} KB)
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4">
                  <Button
                    onClick={handleAnalyze}
                    disabled={!selectedFile || processing}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-900"
                    data-testid="analyze-btn"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <FileSearch className="h-4 w-4 mr-2" />
                        Analyze Document
                      </>
                    )}
                  </Button>
                  {selectedFile && (
                    <Button variant="outline" onClick={clearForm}>
                      Clear
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Results Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Analysis Results
                  {result && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(result.structured_data)}
                    >
                      <Copy className="h-4 w-4 mr-1" /> Copy JSON
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {processing && (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                    <Loader2 className="h-12 w-12 animate-spin mb-4" />
                    <p>Analyzing document with AI...</p>
                    <p className="text-sm">This may take 10-30 seconds</p>
                  </div>
                )}

                {error && !processing && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">Analysis Failed</span>
                    </div>
                    <p className="text-red-600 mt-2">{error}</p>
                  </div>
                )}

                {result && !processing && (
                  <div className="space-y-4">
                    {/* Status */}
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-100 text-green-700">
                        <Check className="h-3 w-3 mr-1" /> Success
                      </Badge>
                      <span className="text-sm text-gray-500">
                        {result.document_type} • {result.result_id?.slice(0, 8)}
                      </span>
                    </div>

                    {/* Structured Data */}
                    {result.structured_data && (
                      <div>
                        <Label className="text-sm">Extracted Data</Label>
                        <div className="mt-1 bg-gray-50 rounded-lg p-3 max-h-96 overflow-auto">
                          <pre className="text-xs whitespace-pre-wrap">
                            {JSON.stringify(result.structured_data, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}

                    {/* Raw Response */}
                    {result.raw_response && (
                      <div>
                        <Label className="text-sm">Raw AI Response</Label>
                        <Textarea
                          value={result.raw_response}
                          readOnly
                          className="mt-1 h-48 text-xs"
                        />
                      </div>
                    )}
                  </div>
                )}

                {!processing && !result && !error && (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                    <FileText className="h-12 w-12 mb-4" />
                    <p>Upload a document and click Analyze</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Analysis History
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadHistory}
                  disabled={loadingHistory}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loadingHistory ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>ID</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Processed</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        {loadingHistory ? 'Loading...' : 'No analysis history'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    history.map(item => (
                      <TableRow key={item.id}>
                        <TableCell className="font-mono text-sm">
                          {item.id?.slice(0, 8)}...
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {item.document_type?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {item.success ? (
                            <Badge className="bg-green-100 text-green-700">Success</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700">Failed</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {new Date(item.processed_at || item.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => copyToClipboard(item.structured_data)}
                              title="Copy JSON"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
